import { useState } from 'react'
import { useAccountCurrency } from '@/lib/use-account-currency'
import { type Resolver } from 'react-hook-form'
import { zodResolverI18n } from '@/lib/zod-resolver-i18n'
import { useTranslation } from 'react-i18next'
import {
	Form, FormControl, FormMessage, FormDialog, FormField, FormItem, FormLabel,
	Input, Textarea,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@rentflowdev/ui'
import { useCreateFinanceInvoiceMutation, useListRentalsQuery } from '@/api'
import { usePersistedForm } from '@/lib/use-persisted-form'
import { extractErrorMessage } from '@/lib/error-message'
import type { InvoiceType, Rental } from '@/types'
import { createInvoiceSchema, DEFAULT_VALUES, type CreateInvoiceFormValues } from './schema'

const INVOICE_TYPES: InvoiceType[] = ['RENT', 'DEPOSIT', 'UTILITIES', 'PENALTY', 'ADDITIONAL', 'ONE_TIME']

interface CreateInvoiceDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	/** When set, the dialog is opened from that rental's own page: the rental
	 *  is already known from context, so the picker is hidden and locked to it. */
	rental?: Rental
}

export function CreateInvoiceDialog({ open, onOpenChange, rental }: CreateInvoiceDialogProps) {
	const { t } = useTranslation()
	const accountCurrency = useAccountCurrency()
	const { data: rentalsData, isLoading: isLoadingRentals } = useListRentalsQuery({ limit: 200 }, { skip: !!rental || !open })
	const [createInvoice, { isLoading }] = useCreateFinanceInvoiceMutation()
	const lockedDefaults = { ...DEFAULT_VALUES, rentalId: rental?.id ?? DEFAULT_VALUES.rentalId }
	const [errorMsg, setErrorMsg] = useState<string | null>(null)

	const { form, resetDraft } = usePersistedForm<CreateInvoiceFormValues>({
		key:           `finance:invoice:create:${rental?.id ?? 'global'}`,
		defaultValues: lockedDefaults,
		resolver:      zodResolverI18n(createInvoiceSchema) as Resolver<CreateInvoiceFormValues>,
		mode:          'onBlur',
		active:        open,
	})

	const rentals = rentalsData?.rentals ?? []
	const selectedRentalId = form.watch('rentalId')
	const selectedRental = rental ?? rentals.find((r) => r.id === selectedRentalId)

	async function onSubmit(values: CreateInvoiceFormValues) {
		setErrorMsg(null)
		try {
			await createInvoice({
				// When opened from a rental page the rental is locked by context —
				// never trust a stale persisted draft over it.
				rentalId:    rental?.id ?? values.rentalId,
				type:        values.type,
				title:       t(`finances.types.${values.type}`),
				amount:      String(values.amount),
				currency:    selectedRental?.currency ?? accountCurrency,
				periodStart: values.periodStart,
				periodEnd:   values.periodEnd,
				dueDate:     values.dueDate,
				// The comment is sent as the invoice's single line item — invoices have
				// no comment field of their own. `qty` is required by the API and has
				// no input here, so it is fixed at one; the invoice total is carried by
				// `amount`, not by quantity × price.
				...(values.comment && {
					items: [{ description: values.comment, amount: String(values.amount), qty: '1' }],
				}),
			}).unwrap()
			resetDraft()
			form.reset(lockedDefaults)
			onOpenChange(false)
		} catch (err) {
			setErrorMsg(extractErrorMessage(err) ?? t('finances.createDialog.errors.createFailed'))
		}
	}

	function handleOpenChange(nextOpen: boolean) {
		if (!nextOpen) {
			setErrorMsg(null)
			resetDraft()
			form.reset(lockedDefaults)
		}
		onOpenChange(nextOpen)
	}

	return (
		<Form {...form}>
			<FormDialog
				open={open}
				onOpenChange={handleOpenChange}
				title={t('finances.createDialog.title')}
				onSubmit={form.handleSubmit(onSubmit)}
				submitLabel={t('finances.createDialog.submit')}
				cancelLabel={t('finances.createDialog.cancel')}
				closeAriaLabel={t('common.close')}
				isSubmitting={isLoading}
			>
				{!rental && (
					<FormField
						control={form.control}
						name="rentalId"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('finances.createDialog.rental')}</FormLabel>
								<Select onValueChange={field.onChange} value={field.value} disabled={isLoadingRentals}>
									<FormControl>
										<SelectTrigger className="w-full">
											<SelectValue placeholder={isLoadingRentals ? t('common.loading') : t('finances.createDialog.rentalPlaceholder')} />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{rentals.map((r) => (
											<SelectItem key={r.id} value={r.id}>
												{r.propertyTitle ?? r.propertyAddress ?? r.id}
												{r.tenantName ? ` — ${r.tenantName}` : ''}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>
				)}

				<FormItem>
					<FormLabel>{t('finances.createDialog.property')}</FormLabel>
					<Input
						readOnly
						value={selectedRental?.propertyTitle ?? selectedRental?.propertyAddress ?? ''}
						placeholder={selectedRentalId ? t('finances.createDialog.notSpecified') : t('finances.createDialog.locked')}
					/>
				</FormItem>

				<FormItem>
					<FormLabel>{t('finances.createDialog.tenant')}</FormLabel>
					<Input
						readOnly
						value={selectedRental?.tenantName ?? ''}
						placeholder={selectedRentalId ? t('finances.createDialog.notSpecified') : t('finances.createDialog.locked')}
					/>
				</FormItem>

				<FormField
					control={form.control}
					name="type"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('finances.createDialog.type')}</FormLabel>
							<Select onValueChange={field.onChange} value={field.value}>
								<FormControl>
									<SelectTrigger className="w-full">
										<SelectValue placeholder={t('finances.createDialog.typePlaceholder')} />
									</SelectTrigger>
								</FormControl>
								<SelectContent>
									{INVOICE_TYPES.map((tp) => (
										<SelectItem key={tp} value={tp}>{t(`finances.types.${tp}`)}</SelectItem>
									))}
								</SelectContent>
							</Select>
							<FormMessage />
						</FormItem>
					)}
				/>

				<div className="grid grid-cols-2 gap-3">
					<FormField
						control={form.control}
						name="amount"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('finances.createDialog.amount')}</FormLabel>
								<FormControl>
									<Input type="number" min={0} step="0.01" {...field} value={field.value ?? ''} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="dueDate"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('finances.createDialog.dueDate')}</FormLabel>
								<FormControl>
									<Input type="date" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<div className="grid grid-cols-2 gap-3">
					<FormField
						control={form.control}
						name="periodStart"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('finances.createDialog.periodStart')}</FormLabel>
								<FormControl>
									<Input type="date" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="periodEnd"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('finances.createDialog.periodEnd')}</FormLabel>
								<FormControl>
									<Input type="date" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<FormField
					control={form.control}
					name="comment"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('finances.createDialog.comment')}</FormLabel>
							<FormControl>
								<Textarea {...field} value={field.value ?? ''} className="min-h-20" />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				{errorMsg && (
					<div className="rounded-(--radius-md) border border-(--color-destructive)/30 bg-(--color-destructive)/5 px-3 py-2">
						<p className="text-sm text-(--color-destructive)">{errorMsg}</p>
					</div>
				)}
			</FormDialog>
		</Form>
	)
}
